import { useState, useEffect } from "react";
import { useHeaderContext } from "../context/headerContext";
import { useDrawerContext } from "../context/drawerContext";
import SubscriptionCard from "../components/SubscriptionCard";
import Button from "../components/Button";
import { useUserContext } from "../context/userContext";
import fetchWithRefresh from "../utils/fetchWithRefresh";
import { useNavigate } from "react-router-dom";
import { set } from "date-fns";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../utils/LogErrors";

export default function UpgradeSubscription() {
  const { setTitle } = useHeaderContext();
  const { setOpen } = useDrawerContext();
  const { user, logout, updateTokens } = useUserContext();
  const [subscriptionLevels, setSubscriptionLevels] = useState([]);
  const navigate = useNavigate();

  const [hasUpgrade, setHasUpgrade] = useState(false);

  useEffect(() => {
    Sentry.startSpan({ name: "Fetch Subscription Levels", op: "subscription" }, async (span) => {
      setTitle("Upgrade Subscription");
      setOpen(false);
  
      const fetchSubscriptionLevels = async () => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Subscription/GetSubscriptionLevels`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
  
          if (!response.ok) {
            const errorMsg = `Failed to fetch subscription levels: ${response.status} - ${response.statusText}`;
            Sentry.captureMessage(errorMsg, "error");
            LogErrors(user, logout, updateTokens, errorMsg);
            throw new Error(errorMsg);
          }
  
          const data = await response.json();
          setSubscriptionLevels(data);
          span.setStatus("ok");
  
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error fetching subscription levels: " + error);
          Sentry.captureException(error);
          toast.error("Failed to fetch subscription levels.");
        }
      };
  
      const fetchAvailableUpgrades = async () => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Cart/SubscriptionProducts?userAccountId=${user.id}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
  
          if (!response.ok) {
            const errorMsg = `Failed to fetch available upgrades: ${response.status} - ${response.statusText}`;
            Sentry.captureMessage(errorMsg, "error");
            LogErrors(user, logout, updateTokens, errorMsg);
            throw new Error(errorMsg);
          }
  
          const data = await response.json();
          setHasUpgrade(data.length > 0);
          span.setStatus("ok");
  
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error fetching available upgrades: " + error);
          Sentry.captureException(error);
          setHasUpgrade(false);
          toast.error("Failed to fetch subscription upgrades.");
        }
      };
  
      fetchSubscriptionLevels();
      fetchAvailableUpgrades();
      span.end();
    });
  
  }, []);
  
  const BackButton = () => {
    navigate(-1);
  }

  return (
    <div className="bg-[#F8F8F8] py-[30px] pl-6 pr-4 flex flex-col h-full">
      <div className="mb-10">
        <p className="text-[12px] leading-[24px] font-semibold tracking-[0.2em] mb-1">
          SPORTS GUARDIAN
        </p>
        <h2>Subscription Levels</h2>
      </div>
      <div className="flex flex-nowrap overflow-x-scroll gap-5 mb-[30px] no-scrollbar">
        {subscriptionLevels?.map((subscription, index) => (
          <SubscriptionCard
            subscription={subscription}
            key={subscription?.SubscriptionId}
            user={user}
            hasUpgrade={user?.subscription?.Name === "Lite" || subscriptionLevels.length === index + 1 ? hasUpgrade : false}
          />
        ))}
      </div>
      <div className="flex flex-col justify-center pb-[80px] gap-5 items-center">
        <Button variant="primary" className="max-w-[260px] w-full" onClick={BackButton}>Back</Button>
        <Button variant="primary" status="nobg" className="max-w-[260px] w-full mx-auto" href={"https://www.sportsguardian.com/member-subscription-levels"} target={"_blank"}>See Full Comparison</Button>
      </div>
    </div>
  );
}